import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { promoService } from '../../services/api';
import { Loader2, UserPlus, Copy, Check, Ticket, Clock, Send, RotateCw } from 'lucide-react';

const Referrals = () => {
    const { language } = useLanguage();

    const [loading, setLoading] = useState(true);
    const [referralCode, setReferralCode] = useState(null);
    const [stats, setStats] = useState({ totalReferred: 0, totalRewards: 0 });
    const [pending, setPending] = useState([]);
    const [codes, setCodes] = useState([]);
    const [copied, setCopied] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviting, setInviting] = useState(false);
    const [inviteStatus, setInviteStatus] = useState(null); // { type: 'success'|'error', message }
    const [resending, setResending] = useState(null); // email currently being resent

    useEffect(() => {
        Promise.all([
            promoService.getMyReferral(),
            promoService.getMyCodes()
        ]).then(([refData, codesData]) => {
            setReferralCode(refData.referralCode);
            setStats(refData.stats || { totalReferred: 0, totalRewards: 0 });
            setPending(refData.pending || []);
            setCodes(codesData.codes || []);
        }).catch(err => {
            console.error('Referrals load error:', err);
        }).finally(() => setLoading(false));
    }, []);

    const handleCopy = () => {
        if (!referralCode) return;
        navigator.clipboard.writeText(referralCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        const email = inviteEmail.trim();
        if (!email || inviting) return;
        setInviting(true);
        setInviteStatus(null);
        try {
            await promoService.invite(email, language);
            setPending(prev => [...prev, email]);
            setInviteEmail('');
            setInviteStatus({
                type: 'success',
                message: language === 'fr' ? `Invitation envoyée à ${email} !` : `Invitation sent to ${email}!`
            });
            setTimeout(() => setInviteStatus(null), 4000);
        } catch (err) {
            const msg = err.response?.data?.error || (language === 'fr' ? "Échec de l'envoi" : 'Failed to send');
            setInviteStatus({ type: 'error', message: msg });
        } finally {
            setInviting(false);
        }
    };

    const handleResend = async (email) => {
        if (resending) return;
        setResending(email);
        try {
            await promoService.resend(email, language);
            setInviteStatus({
                type: 'success',
                message: language === 'fr' ? `Invitation renvoyée à ${email} !` : `Invitation resent to ${email}!`
            });
            setTimeout(() => setInviteStatus(null), 4000);
        } catch (err) {
            const msg = err.response?.data?.error || (language === 'fr' ? "Échec de l'envoi" : 'Failed to send');
            setInviteStatus({ type: 'error', message: msg });
        } finally {
            setResending(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#00bcd4]" />
            </div>
        );
    }

    const referralCodes = codes.filter(c => c.type === 'referral_reward' || c.type === 'referral_welcome');
    const otherCodes = codes.filter(c => c.type !== 'referral_reward' && c.type !== 'referral_welcome');

    return (
        <div className="pb-12">
            {/* Header Banner */}
            <div className="relative overflow-hidden rounded-[2rem] p-10 h-64 bg-gradient-to-br from-[#004d40] via-[#006064] to-[#003135] border border-white/5 !mb-12 shadow-xl">
                <div className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent 50%, rgba(0,0,0,0.5) 50%)', backgroundSize: '4px 100%' }} />

                <div className="relative z-10 h-full flex flex-col justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-[#00bcd4] rounded-2xl shadow-lg shadow-[#00bcd4]/20">
                            <UserPlus className="w-6 h-6 text-black" />
                        </div>
                        <h1 className="text-4xl font-black text-white tracking-tight">
                            {language === 'fr' ? 'Mes parrainages' : 'My referrals'}
                        </h1>
                    </div>
                    <p className="text-white/60 text-lg max-w-md leading-snug">
                        {language === 'fr'
                            ? 'Partage ton code avec tes amis. Ils obtiennent 10% sur leur premier achat, et tu reçois un code de 10% en retour !'
                            : 'Share your code with friends. They get 10% off their first purchase, and you receive a 10% code in return!'}
                    </p>
                </div>

                <div className="absolute right-[-40px] bottom-[-40px] opacity-20">
                    <UserPlus className="w-80 h-80 text-[#00bcd4] stroke-[0.5]" />
                </div>
            </div>

            {/* Referral Code Card */}
            <div className="bg-[#00bcd4] rounded-[1.5rem] p-6 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-lg shadow-[#00bcd4]/10 !mb-12">
                <div className="flex items-center gap-6">
                    <div className="text-center sm:text-left">
                        <p className="text-black/60 text-xs font-bold uppercase tracking-wider mb-1">
                            {language === 'fr' ? 'Ton code de parrainage' : 'Your referral code'}
                        </p>
                        <span className="text-3xl font-black text-black tracking-wider">
                            {referralCode || '---'}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right text-black/80 text-sm font-bold hidden sm:block">
                        <div>{stats.totalReferred} {language === 'fr' ? 'filleul(s)' : 'referral(s)'}</div>
                        <div>{stats.totalRewards} {language === 'fr' ? 'récompense(s)' : 'reward(s)'}</div>
                    </div>
                    <button
                        onClick={handleCopy}
                        className="px-6 py-3 bg-white text-black font-black rounded-full text-sm shadow-md hover:scale-[1.05] transition-transform active:scale-[0.95] flex items-center gap-2"
                    >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {copied
                            ? (language === 'fr' ? 'Copié !' : 'Copied!')
                            : (language === 'fr' ? 'Copier' : 'Copy')
                        }
                    </button>
                </div>
            </div>

            {/* Invite by Email */}
            <div className="bg-white dark:bg-[#121212] rounded-[1.5rem] p-6 border border-gray-200 dark:border-white/5 !mb-12">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                    {language === 'fr' ? 'Inviter par courriel' : 'Invite by email'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {language === 'fr'
                        ? 'Un joli courriel sera envoyé avec votre code de parrainage et la réduction de 10%.'
                        : 'A nice email will be sent with your referral code and the 10% discount.'}
                </p>
                <form onSubmit={handleInvite} className="flex flex-col sm:flex-row gap-3">
                    <input
                        type="email"
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        placeholder={language === 'fr' ? 'adresse@email.com' : 'email@address.com'}
                        required
                        className="flex-1 px-4 py-3 rounded-xl bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00bcd4]/50 focus:border-[#00bcd4] transition"
                    />
                    <button
                        type="submit"
                        disabled={inviting || !inviteEmail.trim()}
                        className="px-6 py-3 bg-[#00bcd4] text-black font-bold rounded-xl text-sm shadow-md hover:scale-[1.03] active:scale-[0.97] transition-transform flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none flex-shrink-0"
                    >
                        {inviting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                        {language === 'fr' ? 'Inviter' : 'Send invite'}
                    </button>
                </form>
                {inviteStatus && (
                    <div className={`mt-3 px-4 py-2.5 rounded-xl text-sm font-medium ${
                        inviteStatus.type === 'success'
                            ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20'
                            : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/20'
                    }`}>
                        {inviteStatus.message}
                    </div>
                )}
            </div>

            {/* Stats on mobile */}
            <div className="sm:hidden bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-4 flex justify-around !mb-8">
                <div className="text-center">
                    <span className="text-2xl font-black text-gray-900 dark:text-white">{stats.totalReferred}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Filleuls' : 'Referrals'}</p>
                </div>
                <div className="text-center">
                    <span className="text-2xl font-black text-gray-900 dark:text-white">{stats.totalRewards}</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{language === 'fr' ? 'Récompenses' : 'Rewards'}</p>
                </div>
            </div>

            {/* Pending Referrals */}
            {pending.length > 0 && (
                <div className="space-y-4 !mt-12">
                    <h3 className="text-xl font-bold text-gray-500 dark:text-gray-400">
                        {language === 'fr' ? 'En attente' : 'Pending'}
                    </h3>
                    <div className="space-y-3">
                        {pending.map((email, idx) => (
                            <div key={idx} className="bg-white dark:bg-[#121212] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 dark:border-white/5">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-white/10 flex items-center justify-center text-gray-500 dark:text-gray-400 font-black text-sm flex-shrink-0">
                                        {email?.[0]?.toUpperCase()}
                                    </div>
                                    <span className="text-gray-900 dark:text-gray-300 font-medium text-sm truncate">{email}</span>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0 self-end sm:self-auto">
                                    <span className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                                        <Clock className="w-3.5 h-3.5" />
                                        {language === 'fr' ? 'En attente' : 'Pending'}
                                    </span>
                                    <button
                                        onClick={() => handleResend(email)}
                                        disabled={resending === email}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[#00bcd4] bg-[#00bcd4]/10 hover:bg-[#00bcd4]/20 rounded-lg transition disabled:opacity-50"
                                    >
                                        {resending === email ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <RotateCw className="w-3 h-3" />
                                        )}
                                        {language === 'fr' ? 'Renvoyer' : 'Resend'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Available Promo Codes */}
            {(referralCodes.length > 0 || otherCodes.length > 0) && (
                <div className="space-y-4 !mt-12">
                    <h3 className="text-xl font-bold text-gray-500 dark:text-gray-400">
                        {language === 'fr' ? 'Mes codes promo disponibles' : 'My available promo codes'}
                    </h3>
                    <div className="space-y-3">
                        {[...referralCodes, ...otherCodes].map(c => (
                            <div key={c.id} className="bg-white dark:bg-[#121212] rounded-2xl p-4 flex items-center justify-between border border-gray-200 dark:border-white/5">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-500/10 flex items-center justify-center">
                                        <Ticket className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <div>
                                        <span className="text-gray-900 dark:text-white font-bold text-sm block">{c.code}</span>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {c.type === 'referral_reward'
                                                ? (language === 'fr' ? 'Récompense parrainage' : 'Referral reward')
                                                : c.type === 'referral_welcome'
                                                    ? (language === 'fr' ? 'Bonus parrainage' : 'Referral bonus')
                                                    : (language === 'fr' ? 'Récompense' : 'Reward')
                                            }
                                            {c.expiresAt && ` · ${language === 'fr' ? 'Expire' : 'Expires'} ${new Date(c.expiresAt).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}`}
                                        </span>
                                    </div>
                                </div>
                                <div className="px-4 py-1.5 bg-emerald-50 dark:bg-emerald-500/10 rounded-full border border-emerald-200 dark:border-emerald-500/20">
                                    <span className="text-emerald-700 dark:text-emerald-400 text-sm font-black">-{c.percentage}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {referralCodes.length === 0 && otherCodes.length === 0 && pending.length === 0 && stats.totalReferred === 0 && (
                <div className="!mt-12 bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 p-8 text-center">
                    <UserPlus className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {language === 'fr'
                            ? 'Partage ton code pour commencer à parrainer !'
                            : 'Share your code to start referring friends!'}
                    </p>
                </div>
            )}
        </div>
    );
};

export default Referrals;
